import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, GreenButton, Input } from '~Styles';
import gql from 'graphql-tag';
import { useButton, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { useMutation } from '@apollo/client';
import Loader from '~ComponentsImproved/Loader';
import styled from '@emotion/styled';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import * as actions from '~actions';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { INVOICE_TYPE } from '../../../../__queries__';
import { CancelButton } from '../SelectedUser';
import {
  CreateUser,
  CreateUserVariables,
  UpdateUserMetadata,
  UpdateUserMetadataVariables,
} from './__queries__';

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CREATE_USER = gql`
  mutation CreateUser($data: UserCreateInput!) {
    createUser(data: $data) {
      id
    }
  }
`;

const UPDATE_USER_METADATA = gql`
  mutation UpdateUserMetadata($input: UpdateUserMetadataInput!) {
    updateUserMetadata(input: $input) {
      id
      success
    }
  }
`;

interface Props {
  onUserCreated: (id: string) => void;
  onCancel: () => void;
}

/** Creates a new customer for manual invoice flow */
const CreateNewUser: FC<Props> = ({ onUserCreated, onCancel }) => {
  const { i18n } = useI18n();
  const logger = useLogger('create-new-customer');
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.company);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);

  const isHandy = window.innerWidth <= 600;

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const orgNumberRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCompany, setIsCompany] = useState<boolean>(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);

  const [createUser] = useMutation<CreateUser, CreateUserVariables>(CREATE_USER);
  const [updateUserMetadata] = useMutation<UpdateUserMetadata, UpdateUserMetadataVariables>(
    UPDATE_USER_METADATA,
  );

  const cancelButton = useButton({
    onClick: () => {
      onCancel();
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    const name = nameRef.current?.value || '';
    const zip = zipRef.current?.value || '';
    const orgNumber = orgNumberRef.current?.value || '';

    /** The following is optional fields */
    const email = emailRef.current?.value || undefined;
    const phone = phoneRef.current?.value || undefined;
    const address = addressRef.current?.value || undefined;
    const city = cityRef.current?.value || undefined;

    try {
      const res = await createUser({
        variables: {
          data: {
            name,
            email,
            phone,
            language: defaultLanguage || 'en',
            company: {
              connect: { id: companyId },
            },
            creditBank: {
              create: {
                amount: 0,
              },
            },
          },
        },
      });

      if (!res.data?.createUser?.id) {
        throw new Error('Failed to create user');
      }

      const metadataRes = await updateUserMetadata({
        variables: {
          input: {
            userId: res.data.createUser.id,
            companyId,
            address,
            city,
            zip,
            orgNumber,
            invoiceType: isCompany ? INVOICE_TYPE.COMPANY : INVOICE_TYPE.PRIVATE,
          },
        },
      });

      if (!metadataRes.data?.updateUserMetadata?.success) {
        throw new Error('Failed to update user metadata');
      }

      dispatch(actions.addToast(`${name} was created successfully!`, true));
      onUserCreated(res.data.createUser.id);
    } catch (error) {
      logger.error(`Failed to create customer: ${error ? error.toString() : ''}`);
      setError(error ? error.toString() : i18n('Error.SomethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * We want to use ref here to provide better performance for handheld devices.
   * The following useEffect just checks every 100ms if all required fields are filled to enable/disable the submit button.
   * Which triggers only one re-render of the form when the form gets enabled/disabled instead of on every keystroke
   */
  useEffect(() => {
    const checkDisabled = () => {
      const disabled =
        loading ||
        !nameRef.current?.value?.trim() ||
        !zipRef.current?.value?.trim() ||
        !orgNumberRef.current?.value?.trim();
      setIsSubmitDisabled(disabled);
    };

    checkDisabled();
    const interval = setInterval(checkDisabled, 100);
    return () => clearInterval(interval);
  }, [loading]);

  return (
    <Box padding={isHandy ? 'xs' : 'medium'}>
      <FormWrapper autoComplete="off" onSubmit={handleSubmit}>
        <Input
          ref={nameRef}
          id="name"
          name="name"
          placeholder={i18n('Invoice.Name')}
          required
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={emailRef}
          id="email"
          name="email"
          type="email"
          placeholder={i18n('Invoice.Email.Optional')}
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={phoneRef}
          id="phone"
          name="phone"
          type="tel"
          placeholder={i18n('Invoice.Phone.Optional')}
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={addressRef}
          id="address"
          name="address"
          placeholder={i18n('Invoice.Adress.Optional')}
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={cityRef}
          id="city"
          name="city"
          placeholder={i18n('Invoice.City.Optional')}
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={zipRef}
          id="zip"
          name="zip"
          placeholder={i18n('Invoice.ZipCode')}
          required
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Input
          ref={orgNumberRef}
          id="orgNumber"
          name="orgNumber"
          placeholder={i18n('Invoice.OrgNo')}
          dark
          padding="small"
          fluid
          marginBottom="xs"
        />

        <Box marginBottom="small" fluid>
          <RadioButtonSelect
            data={{
              containerLabel: i18n('CustomerForCheckout.CustomerType'),
              name: 'customerType',
              buttons: [
                {
                  id: 'private',
                  label: i18n('CustomerForCheckout.CustomerType.Private'),
                  value: false,
                },
                {
                  id: 'company',
                  label: i18n('General.Company'),
                  value: true,
                },
              ],
            }}
            selected={isCompany ? 'company' : 'private'}
            handleChange={(value) => setIsCompany(value)}
          />
        </Box>

        <Box
          display="flex"
          flexDirection="row"
          gap="xs"
          marginTop="small"
          alignItems="center"
          fluid>
          <CancelButton {...cancelButton} fluid>
            {i18n('General.GoBack')}
          </CancelButton>
          <GreenButton fluid type="submit" disabled={isSubmitDisabled}>
            {loading ? (
              <Loader size="sm" color="var(--colorWhite)" />
            ) : isHandy ? (
              i18n('Onboarding.Select.Create')
            ) : (
              i18n('CustomerForCheckout.CreateAndSelect')
            )}
          </GreenButton>
        </Box>

        {error && (
          <Box padding="small">
            <Paragraph level={4} bottom={0} color="var(--colorUIRed)">
              {error}
            </Paragraph>
          </Box>
        )}
      </FormWrapper>
    </Box>
  );
};

export default CreateNewUser;
